import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { signInAnonymously, signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, functions } from '../../lib/firebase';

/**
 * 管理者の独自招待トークンの着地ページ (公開ルート `/auth/invite`、AuthGate の外)。
 *
 * マスターが発行した招待 / PW 再発行リンク (`?token=...`) の着地点。Firebase 既定リンク
 * (1 時間固定・別ドメイン) の代わりに、マスターが期限を指定できる独自トークン方式を使う
 * (functions/src/admin/adminInvites.ts)。本ページは:
 *   1. verifyAdminInvite でトークンを検証し email / 期限を取得 (消費しない)
 *   2. 新パスワードを入力 → acceptAdminInvite で PW 設定 (トークンは単回使用)
 *   3. そのまま email+password で自動サインイン → ホームへ
 *
 * AuthActionPage (Firebase oobCode 用) と役割が近いが、こちらは独自トークン callable を叩く。
 * 招待された管理者は未認証なので、本ページは必ず AuthGate の外側 (公開ルート) に置く。
 */

type Stage = 'verifying' | 'form' | 'submitting' | 'error';

const MIN_PASSWORD_LENGTH = 8;

/** callable が throw する短いエラー識別子 → 日本語メッセージ。 */
function inviteErrorMessage(rawMessage: string): string {
  const m = rawMessage || '';
  if (m.includes('invite-expired')) {
    return 'この招待リンクの有効期限が切れています。マスター管理者に再発行を依頼してください。';
  }
  if (m.includes('invite-used')) {
    return 'この招待リンクは既に使用済みです。サインインできない場合はマスター管理者に再発行を依頼してください。';
  }
  if (m.includes('invite-user-not-found')) {
    return 'アカウントが見つかりません。マスター管理者にお問い合わせください。';
  }
  if (m.includes('invite-not-found') || m.includes('invite-invalid')) {
    return 'この招待リンクは無効です。マスター管理者に再発行を依頼してください。';
  }
  if (m.includes('weak-password')) {
    return `パスワードは ${MIN_PASSWORD_LENGTH} 文字以上で設定してください。`;
  }
  return '処理に失敗しました。マスター管理者にお問い合わせください。';
}

export function AuthInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const token = params.get('token') ?? '';

  const [stage, setStage] = useState<Stage>('verifying');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // ─────────────────────────────── マウント時: トークンを検証して email を取得 (消費しない)
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setErrorMessage('リンクが正しくありません（トークンがありません）。');
        setStage('error');
        return;
      }
      try {
        // 招待された本人はまだ未認証。verify/accept callable を確実に呼べるよう、
        // 先に匿名サインインしておく (GCP 組織ポリシーで未認証 invoker が制限されていても動くようにする
        // 防御。匿名認証はこのプロジェクトで有効=ユーザーアプリが使用)。accept 後に email+password で
        // サインインし直すため匿名セッションは置き換わる。失敗しても callable 自体は試行する。
        if (!auth.currentUser) {
          await signInAnonymously(auth).catch(() => {
            /* 匿名認証が無効でも未認証 invoker が許可されていれば動く。握りつぶして続行。 */
          });
        }
        const verify = httpsCallable<{ token: string }, { email: string }>(
          functions,
          'verifyAdminInvite',
        );
        const res = await verify({ token });
        if (cancelled) return;
        setEmail(res.data.email);
        setStage('form');
      } catch (e) {
        if (cancelled) return;
        setErrorMessage(inviteErrorMessage((e as Error).message ?? ''));
        setStage('error');
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ─────────────────────────────── 送信: PW 確定 → 自動ログイン → ホーム
  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast('warning', `パスワードは ${MIN_PASSWORD_LENGTH} 文字以上で設定してください`);
      return;
    }
    if (password !== confirm) {
      toast('warning', 'パスワードが一致しません');
      return;
    }
    setStage('submitting');
    try {
      const accept = httpsCallable<{ token: string; password: string }, { email: string }>(
        functions,
        'acceptAdminInvite',
      );
      const res = await accept({ token, password });
      // 自動ログイン: 設定したばかりの認証情報でそのままサインイン。
      await signInWithEmailAndPassword(auth, res.data.email, password);
      // role / districts の custom claim を即時反映させるため token を強制更新。
      await auth.currentUser?.getIdToken(true);
      toast('success', 'パスワードを設定しサインインしました');
      navigate('/', { replace: true });
    } catch (e) {
      setErrorMessage(inviteErrorMessage((e as Error).message ?? ''));
      setStage('error');
    }
  }

  return (
    <div className="mx-auto max-w-md py-12">
      <header className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-slate-900">CitizenWalk Admin</h1>
        <p className="text-sm text-slate-500 mt-1">管理者パスワードの設定</p>
      </header>

      <Card padding="lg">
        {stage === 'verifying' && <p className="text-sm text-slate-500">リンクを確認しています…</p>}

        {stage === 'error' && (
          <div>
            <p className="text-sm text-red-700 bg-red-50 rounded p-3 border border-red-200">
              {errorMessage}
            </p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="mt-4 text-sm text-primary-600 hover:text-primary-700 hover:underline"
            >
              サインイン画面へ
            </button>
          </div>
        )}

        {(stage === 'form' || stage === 'submitting') && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{email}</span>{' '}
              のパスワードを設定してください。設定後、自動的にサインインします。
            </p>

            <label className="block">
              <span className="text-xs font-semibold text-slate-700">新しいパスワード</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                autoComplete="new-password"
                placeholder={`${MIN_PASSWORD_LENGTH} 文字以上`}
                required
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">パスワード（確認）</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
                autoComplete="new-password"
                required
              />
            </label>

            <Button
              variant="primary"
              size="md"
              type="submit"
              disabled={stage === 'submitting'}
              className="w-full"
            >
              {stage === 'submitting' ? '設定中…' : 'パスワードを設定してサインイン'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
